import { getIdentifier, getReferenceString, MedplumClient, resolveId } from "@medplum/core";
import {
  Bundle,
  BundleEntry,
  List,
  QuestionnaireResponse,
  QuestionnaireResponseItem,
  Task,
} from "@medplum/fhirtypes";

import { addDays, formatISO } from "date-fns";

import { Systems } from "@/fhir/enums/systems";
import { POSTPARTUM_TRACKER_DAYS } from "@/pregnancy/constants/postpartum";
import { postpartumWeeksAndDaysAt } from "@/pregnancy/utils/postpartum";
import { middleOfDay } from "@/utils/datetime";
import { getResourcesFromBundle, joinTokenString } from "@/utils/medplum";

const POSTPARTUM_TRACKER_CODE = "postpartum-tracker";

/**
 * Retrieves the postpartum-tracker `Task` linked to a pregnancy record `List`.
 *
 * Looks up the entry whose `flag` matches the postpartum-tracker code, reads the
 * referenced `Task`, and returns it only when still `in-progress`. Returns
 * `undefined` if no entry is found, the Task does not exist, or it is no longer
 * active.
 *
 * @param medplum - Authenticated `MedplumClient`
 * @param list - Pregnancy record `List` resource (retired or current)
 * @returns The linked in-progress postpartum-tracker `Task`, or `undefined`
 */
export const fetchPostpartumTrackerFromPregnancyRecord = async (
  medplum: MedplumClient,
  list: List,
): Promise<Task | undefined> => {
  const entry = list.entry?.find((listEntry) =>
    listEntry.flag?.coding?.some(
      (coding) => coding.system === Systems.QH && coding.code === POSTPARTUM_TRACKER_CODE,
    ),
  );

  const taskId = resolveId(entry?.item);
  if (!taskId) return undefined;

  try {
    return await medplum.readResource("Task", taskId);
  } catch {
    return undefined;
  }
};

/**
 * Re-reads a pregnancy `List` from FHIR and returns its linked in-progress
 * postpartum-tracker `Task`.
 *
 * Invalidates Task/List search caches first so callers see updates written
 * outside Redux (for example after GTPAL delivery-date saves).
 *
 * @param medplum - Authenticated `MedplumClient`
 * @param listId - FHIR `List` ID for the pregnancy record
 * @returns The linked in-progress postpartum-tracker `Task`, or `undefined`
 */
export async function fetchPostpartumTrackerForPregnancyListId(
  medplum: MedplumClient,
  listId: string,
): Promise<Task | undefined> {
  medplum.invalidateSearches("Task");
  medplum.invalidateSearches("List");

  let list: List | undefined;
  try {
    list = await medplum.readResource("List", listId);
  } catch {
    list = undefined;
  }

  if (!list) return undefined;

  return fetchPostpartumTrackerFromPregnancyRecord(medplum, list);
}

/**
 * Cancels all in-progress postpartum-tracker Tasks for the given patient.
 *
 * Called before creating a new tracker so that multiple births never leave
 * orphaned in-progress tasks.
 *
 * @param medplum - Authenticated `MedplumClient`
 * @param clientId - FHIR `Patient` ID
 */
export const cancelActivePostpartumTrackers = async (
  medplum: MedplumClient,
  clientId: string,
): Promise<void> => {
  const activeTasks = await medplum.searchResources("Task", {
    subject: `Patient/${clientId}`,
    status: "in-progress",
    code: joinTokenString(Systems.QH, POSTPARTUM_TRACKER_CODE),
  });

  await Promise.all(
    activeTasks.map((task) => medplum.updateResource({ ...task, status: "cancelled" })),
  );
};

/**
 * Creates a new postpartum-tracker Task for the given patient, cancelling any
 * previously in-progress trackers first, then linking the new Task back to the
 * closed pregnancy record's List so it can be retrieved via
 * `fetchPostpartumTrackerFromPregnancyRecord`.
 *
 * @param medplum - Authenticated `MedplumClient`
 * @param clientId - FHIR `Patient` ID
 * @param closeoutDate - Delivery/close-out date; becomes `executionPeriod.start`
 * @param identifier - Unique identifier value shared with the pregnancy close-out
 * @param pregnancyRecordId - Optional ID of the now-retired pregnancy `List` to link;
 *   when provided the List is re-read from FHIR (to get the latest version) and the
 *   Task reference is appended to `List.entry`
 * @returns An object containing the newly created `Task` and, when `pregnancyRecordId`
 *   was supplied, the updated pregnancy `List` resource so callers can sync Redux
 */
export const createPostpartumTracker = async (
  medplum: MedplumClient,
  clientId: string,
  closeoutDate: Date,
  identifier: string,
  pregnancyRecordId?: string,
): Promise<{ task: Task; updatedPregnancyList?: List }> => {
  await cancelActivePostpartumTrackers(medplum, clientId);

  const normalizedCloseoutDate = middleOfDay(closeoutDate)!;

  const task = await medplum.createResource<Task>({
    resourceType: "Task",
    identifier: [{ system: Systems.IdentifierType, value: identifier }],
    status: "in-progress",
    intent: "order",
    code: { coding: [{ system: Systems.QH, code: POSTPARTUM_TRACKER_CODE }] },
    executionPeriod: {
      start: formatISO(normalizedCloseoutDate),
      end: formatISO(addDays(normalizedCloseoutDate, POSTPARTUM_TRACKER_DAYS)),
    },
    for: { reference: `Patient/${clientId}` },
  });

  if (!pregnancyRecordId) return { task };

  let list: List | undefined;
  try {
    list = await medplum.readResource("List", pregnancyRecordId);
  } catch {
    list = undefined;
  }

  if (!list) return { task, updatedPregnancyList: undefined };

  const updatedPregnancyList = await medplum.updateResource({
    ...list,
    entry: [
      ...(list.entry ?? []),
      {
        item: { reference: getReferenceString(task) },
        flag: task.code,
        date: formatISO(normalizedCloseoutDate),
      },
    ],
  });

  return { task, updatedPregnancyList };
};

/**
 * Returns the current postpartum age (weeks, days, totalDays) derived from a
 * running postpartum-tracker Task, evaluated against today.
 *
 * Falls back to `{ weeks: 0, days: 0, totalDays: 0 }` when the task has no
 * execution start date or the tracker has exceeded the postpartum window.
 *
 * @param tracker - In-progress postpartum-tracker `Task` resource
 */
export const trackerToWeeksAndDays = (
  tracker: Task,
): { weeks: number; days: number; totalDays: number } => {
  const start = tracker.executionPeriod?.start;
  if (!start) return { weeks: 0, days: 0, totalDays: 0 };

  return postpartumWeeksAndDaysAt(start) ?? { weeks: 0, days: 0, totalDays: 0 };
};

export type SyncPostpartumTrackerFromGtpalResult =
  | { ok: true; data: Task[] }
  | {
      ok: false;
      reason: "missing_subject" | "no_delivery_dates" | "no_matching_tasks" | "update_failed";
      details?: unknown;
    };

/**
 * Updates in-progress postpartum-tracker Tasks when GTPAL pregnancy-detail delivery
 * dates change, using the same identifier matching as the GTPAL bot.
 *
 * @param medplum - Authenticated Medplum client
 * @param questionnaireResponse - Saved GTPAL QuestionnaireResponse
 * @returns Updated tasks, or a failure reason when prerequisites are missing
 */
export async function syncPostpartumTrackerFromGtpalQuestionnaireResponse(
  medplum: MedplumClient,
  questionnaireResponse: QuestionnaireResponse,
): Promise<SyncPostpartumTrackerFromGtpalResult> {
  const subjectReference = questionnaireResponse.subject?.reference;
  if (!subjectReference) {
    return { ok: false, reason: "missing_subject" };
  }

  const deliveryDatesByIdentifier = getGtpalDeliveryDatesByIdentifier(questionnaireResponse);
  if (Object.keys(deliveryDatesByIdentifier).length === 0) {
    return { ok: false, reason: "no_delivery_dates" };
  }

  try {
    const identifiers = Object.keys(deliveryDatesByIdentifier);
    const postpartumTasks = await findInProgressPostpartumTrackerTasks(medplum, subjectReference);

    const closeoutTasks = postpartumTasks.filter((task) => {
      const identifier = getIdentifier(task, Systems.IdentifierType);
      return identifier ? identifiers.includes(identifier) : false;
    });

    if (closeoutTasks.length === 0) {
      return { ok: false, reason: "no_matching_tasks" };
    }

    const bundleEntries = buildPostpartumTrackerUpdateBundleEntries(
      closeoutTasks,
      deliveryDatesByIdentifier,
    );

    const response = await medplum.executeBatch({
      resourceType: "Bundle",
      type: "transaction",
      entry: bundleEntries,
    });

    medplum.invalidateSearches("Task");

    const updatedTasks = getResourcesFromBundle<Task>("Task", response as Bundle<Task>);

    return { ok: true, data: updatedTasks };
  } catch (error) {
    return { ok: false, reason: "update_failed", details: error };
  }
}

/**
 * Builds FHIR transaction bundle entries that update postpartum-tracker Task
 * execution periods from GTPAL pregnancy-detail delivery dates.
 *
 * @param tasks - In-progress postpartum-tracker Tasks to update
 * @param deliveryDatesByIdentifier - Closeout identifier to delivery date map
 * @returns PUT bundle entries for each task with a matching delivery date
 */
function buildPostpartumTrackerUpdateBundleEntries(
  tasks: Task[],
  deliveryDatesByIdentifier: Record<string, string>,
): BundleEntry<Task>[] {
  return tasks.reduce<BundleEntry<Task>[]>((entries, task) => {
    const identifier = getIdentifier(task, Systems.IdentifierType);
    const startDate = identifier ? deliveryDatesByIdentifier[identifier] : undefined;
    if (!startDate || !task.id) return entries;

    const normalizedStartDate = middleOfDay(startDate)!;

    entries.push({
      request: {
        method: "PUT",
        url: `Task/${task.id}`,
      },
      resource: {
        ...task,
        executionPeriod: {
          start: formatISO(normalizedStartDate),
          end: formatISO(addDays(normalizedStartDate, POSTPARTUM_TRACKER_DAYS)),
        },
      },
    });

    return entries;
  }, []);
}

export function getGtpalDeliveryDatesByIdentifier(
  questionnaireResponse: QuestionnaireResponse,
): Record<string, string> {
  return findQuestionnaireResponseItems(
    questionnaireResponse.item,
    "pregnancyDetailsFormset",
  ).reduce<Record<string, string>>((acc, formsetItem) => {
    const identifier = getFormsetAnswerString(formsetItem, "pregnancyDetailsFormsetIdentifier");
    const date = getFormsetAnswerString(formsetItem, "pregnancyDetailsFormsetDate");

    if (identifier && date) {
      acc[identifier] = date;
    }

    return acc;
  }, {});
}

function findQuestionnaireResponseItems(
  items: QuestionnaireResponseItem[] | undefined,
  linkId: string,
): QuestionnaireResponseItem[] {
  const result: QuestionnaireResponseItem[] = [];

  for (const item of items ?? []) {
    if (item.linkId === linkId) {
      result.push(item);
    }
    if (item.item) {
      result.push(...findQuestionnaireResponseItems(item.item, linkId));
    }
  }

  return result;
}

function getFormsetAnswerString(
  formsetItem: QuestionnaireResponseItem,
  linkId: string,
): string | undefined {
  const answer = formsetItem.item?.find((item) => item.linkId === linkId)?.answer?.[0];
  if (!answer) return undefined;

  if (answer.valueDate) return answer.valueDate;
  if (answer.valueDateTime) return answer.valueDateTime;
  if (answer.valueString) return answer.valueString;

  return undefined;
}

async function findInProgressPostpartumTrackerTasks(
  medplum: MedplumClient,
  subjectReference: string,
): Promise<Task[]> {
  return medplum.searchResources("Task", {
    subject: subjectReference,
    status: "in-progress",
    code: joinTokenString(Systems.QH, POSTPARTUM_TRACKER_CODE),
  });
}
